import Business from '@material-ui/icons/Business';
import Add from '@material-ui/icons/Add';
import Done from '@material-ui/icons/Done';
import classNames from 'classnames';
import React from 'react';
import { BodyS, Caption } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import AlternativeSelect from 'components/AlternativeSelect/AlternativeSelect';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/addUserDialogStyle';
import { makeStyles } from '@material-ui/core';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { MAX_ADMIN_COUNT } from 'utils/const/users';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { UserType } from 'views/Users/UsersList';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IOrganizationAndDepartmentTabProps {
	accounts: SingleCorpAccount[];
	account?: SingleCorpAccount;
	assignedAccount: types.users.IAssignedAccount | null;
	setAssignedAccount: (account: types.users.IAssignedAccount | null) => void;
	setSelectedAccounts: (accounts: string[]) => void;
	filteredDepartments: SingleDepartment[];
	selectedDepartment: SingleDepartment | null;
	setSelectedDepartment: (department: SingleDepartment | null) => void;
	selectedUserType: string;
}

const OrganizationAndDepartmentTab = ({
	accounts,
	account,
	assignedAccount,
	setAssignedAccount,
	setSelectedAccounts,
	filteredDepartments,
	selectedDepartment,
	setSelectedDepartment,
	selectedUserType,
}: IOrganizationAndDepartmentTabProps) => {
	const classes = useStyles();

	const availableAccounts = (selectedUserType === UserType.ADMIN
		? accounts?.filter((account) => Number(account.adminsCount) < MAX_ADMIN_COUNT)
		: accounts
	)?.map((account) => ({
		id: account.accountId,
		name: account.accountName,
	}));

	return (
		<div>
			<div className={classes.assignToAccount}>
				<BodyS disableMargin>{translate('assign.to.organization')}</BodyS>
			</div>
			{!account && accounts.length > 1 ? (
				<AlternativeSelect
					className={classes.select}
					label={translate('organization.name')}
					value={
						<div className={classes.selectedItem}>
							<CustomAvatar
								accountId={assignedAccount ? assignedAccount.id : undefined}
								alt="Company Logo"
								placeholderIcon={<Business />}
								customClasses={classes.selectedItemIcon}
							/>
							<BodyS>{assignedAccount ? assignedAccount.name : translate('all.organizations')}</BodyS>
						</div>
					}
					onClick={(item) => {
						setAssignedAccount(item);
						item.disabled ? setSelectedAccounts([]) : setSelectedAccounts([item.id]);
					}}
					items={availableAccounts}
					selectedItems={assignedAccount ? [assignedAccount.id] : []}
				/>
			) : (
				<div className={classes.selectedItem}>
					<Business />
					<BodyS>{assignedAccount ? assignedAccount.name : translate('organizations')}</BodyS>
				</div>
			)}
			{assignedAccount && (
				<div className={classes.departmentsWrapper}>
					{filteredDepartments.length <= 0 && (
						<BodyS color="disabled-light">
							{translate('organizations.no.departments.in.this.organization')}
						</BodyS>
					)}
					{filteredDepartments.length > 0 && (
						<>
							<BodyS>{translate('assign.to.department.optional')}</BodyS>
							<div>
								{filteredDepartments.map((department) => (
									<div
										key={department.deptId}
										style={{
											backgroundColor: department.uiColor,
										}}
										className={classNames({
											[classes.notSelected]:
												!selectedDepartment || selectedDepartment.deptId !== department.deptId,
										})}
										onClick={() => {
											if (selectedDepartment && selectedDepartment.deptId === department.deptId) {
												setSelectedDepartment(null);
											} else {
												setSelectedDepartment(department);
											}
										}}
									>
										<Caption color="primary-dark" disableMargin>
											{department.name}
										</Caption>
										{selectedDepartment && selectedDepartment.deptId === department.deptId ? (
											<Done />
										) : (
											<Add />
										)}
									</div>
								))}
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default OrganizationAndDepartmentTab;
